import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  alpha,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  SavingsOutlined,
  PercentOutlined,
  CalendarMonthOutlined,
  InsightsOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';
import { fetchAllRows } from '../utils/fetchAllRows';
import {
  INCOME_COLOR,
  EXPENSE_COLOR,
  OTHER_COLOR,
  TOP_CATEGORIES,
  periodMonths,
  analyse,
  assignColors,
  compactRupees,
  formatRupees,
  formatPercent,
} from '../utils/transactionAnalysis';
import { SummaryTile } from './utilities/FastagManager';
import { Legend, ChartCard, chartSx, money } from '../components/analysis/ChartParts';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';

const TransactionAnalysis = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [searchParams, setSearchParams] = useSearchParams();

  const [period, setPeriod] = useState(searchParams.get('period') || 'last12');
  const [walletId, setWalletId] = useState('all');
  const [type, setType] = useState(searchParams.get('type') === 'income' ? 'income' : 'expense');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [wallets, setWallets] = useState([]);
  const [firstYear, setFirstYear] = useState(new Date().getFullYear());
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const tour = useTour('analysis', { ready: !loading });

  const { months, from, to } = useMemo(() => periodMonths(period), [period]);

  // Wallets for the filter, and the first year with data for the period list
  useEffect(() => {
    if (!user) return;
    supabase.from('wallets').select('id, name, type, is_archived').eq('user_id', user.id).order('name')
      .then(({ data }) => setWallets(data || []));
    supabase.from('transactions').select('date').eq('user_id', user.id).order('date', { ascending: true }).limit(1)
      .then(({ data }) => { if (data?.[0]?.date) setFirstYear(Number(String(data[0].date).slice(0, 4))); });
  }, [supabase, user]);

  // All income / expense transactions in the period (every wallet; the wallet filter is applied below)
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchAllRows(() => supabase
      .from('transactions')
      .select('id, type, amount, date, wallet_id, category_id, category:categories (id, name)')
      .eq('user_id', user.id)
      .in('type', ['income', 'expense'])
      .gte('date', from)
      .lte('date', to)
      .order('date', { ascending: true })
      .order('id', { ascending: true }))
      .then((rows) => { if (!cancelled) setTransactions(rows); })
      .catch((error) => {
        console.error('Error loading transactions for analysis:', error);
        if (!cancelled) { setTransactions([]); setLoadError('Could not load your transactions. Please try again.'); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [supabase, user, from, to]);

  const filtered = useMemo(
    () => (walletId === 'all' ? transactions : transactions.filter(t => t.wallet_id === walletId)),
    [transactions, walletId],
  );
  const result = useMemo(() => analyse(filtered, months, type), [filtered, months, type]);
  // Colours come from the ranking across ALL wallets, so a category keeps its colour when the wallet changes
  const colorRanking = useMemo(() => analyse(transactions, months, type).ranked.map(c => c.name), [transactions, months, type]);
  const colors = useMemo(() => assignColors(colorRanking), [colorRanking]);

  const { rows, ranked, totals } = result;
  const hasData = rows.some(r => r.income || r.expense);
  const typeLabel = type === 'expense' ? 'Expenses' : 'Income';

  // Stacked chart: the top categories (by all-wallet rank) + "Other"
  const stackNames = colorRanking.slice(0, TOP_CATEGORIES).filter(name => ranked.some(c => c.name === name));
  const otherNames = ranked.map(c => c.name).filter(name => !stackNames.includes(name));
  const stackSeries = [
    ...stackNames.map(name => ({
      id: name, label: name, color: colors[name], stack: 'categories',
      data: rows.map(r => r.categories[name] || 0), valueFormatter: money,
    })),
    ...(otherNames.length ? [{
      id: '__other', label: `Other (${otherNames.length})`, color: OTHER_COLOR, stack: 'categories',
      data: rows.map(r => otherNames.reduce((sum, name) => sum + (r.categories[name] || 0), 0)), valueFormatter: money,
    }] : []),
  ];

  // Category trend: the picked category, or the biggest one
  const trendCategory = ranked.some(c => c.name === selectedCategory) ? selectedCategory : ranked[0]?.name;
  const trendData = rows.map(r => r.categories[trendCategory] || 0);
  const trendAverage = trendData.reduce((a, b) => a + b, 0) / (totals.activeMonths || 1);
  const maxRanked = ranked[0]?.total || 1;

  const updateParam = (key, value, fallback) => {
    const next = new URLSearchParams(searchParams);
    if (value === fallback) next.delete(key); else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const thisYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = thisYear; y >= Math.min(firstYear, thisYear); y -= 1) yearOptions.push(String(y));
  const activeWallets = wallets.filter(w => !w.is_archived);
  const archivedWallets = wallets.filter(w => w.is_archived);
  const xLabels = rows.map(r => r.label);
  const xBand = (gap) => [{ scaleType: 'band', data: xLabels, categoryGapRatio: gap, barGapRatio: 0.15, tickLabelStyle: { fontSize: 12 } }];
  const yMoney = [{ valueFormatter: (v) => compactRupees(v) }];

  return (
    <Box>
      {/* Sub-header (the page title and tabs are in Analysis.js) */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">Income and spending, month by month and by category</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={tour.start} />
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/transactions')}>Transactions</Button>
        </Box>
      </Box>

      {/* Filters: one row, scoping everything below */}
      <Paper data-tour="analysis-filters" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select size="small" label="Period" value={period} sx={{ minWidth: 170, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}
          onChange={(e) => { setPeriod(e.target.value); updateParam('period', e.target.value, 'last12'); }}
        >
          <MenuItem value="last12">Last 12 months</MenuItem>
          <MenuItem value="last6">Last 6 months</MenuItem>
          {yearOptions.map(y => <MenuItem key={y} value={y}>{y === String(thisYear) ? `This year (${y})` : y}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Wallet" value={walletId} sx={{ minWidth: 190, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}
          onChange={(e) => setWalletId(e.target.value)}
        >
          <MenuItem value="all">All wallets</MenuItem>
          {activeWallets.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          {archivedWallets.map(w => <MenuItem key={w.id} value={w.id}>{w.name} (archived)</MenuItem>)}
        </TextField>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 'auto' } }}>
          <Typography variant="body2" color="text.secondary">Categories:</Typography>
          <ToggleButtonGroup
            exclusive size="small" value={type}
            onChange={(e, value) => { if (value) { setType(value); setSelectedCategory(null); updateParam('type', value, 'expense'); } }}
            aria-label="Category charts show"
          >
            <ToggleButton value="expense">Expenses</ToggleButton>
            <ToggleButton value="income">Income</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {loadError && <Alert severity="error" sx={{ mb: 2.5 }}>{loadError}</Alert>}

      {loading && !transactions.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !hasData ? (
        <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <InsightsOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>No income or expenses in this period</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick another period or wallet, or add transactions to see your analysis.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/transactions')}>Go to Transactions</Button>
        </Card>
      ) : (
        // Hold the previous render (dimmed) while a new period loads: no layout jump
        <Box sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity .2s' }}>
          {/* Headline numbers */}
          <Paper data-tour="analysis-summary" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
            <SummaryTile icon={<TrendingUp />} color="primary" value={formatRupees(totals.income)} label="Income" />
            <SummaryTile icon={<TrendingDown />} color="warning" value={formatRupees(totals.expense)} label="Expenses" />
            <SummaryTile icon={<SavingsOutlined />} color={totals.net >= 0 ? 'success' : 'error'} value={formatRupees(totals.net)} label={totals.net >= 0 ? 'Saved' : 'Overspent'} />
            <SummaryTile icon={<PercentOutlined />} color="info" value={formatPercent(totals.rate)} label="Savings rate" />
            <SummaryTile icon={<CalendarMonthOutlined />} color="secondary" value={formatRupees(totals.avgExpense)} label={`Avg monthly spend (${totals.activeMonths} mo)`} />
          </Paper>

          {/* 1. Income vs expenses */}
          <ChartCard
            data-tour="analysis-monthly"
            title="Income vs Expenses"
            subtitle="Each month's income and spending. Transfers between your own wallets (like card bill payments) aren't counted."
          >
            <Legend items={[{ label: 'Income', color: INCOME_COLOR }, { label: 'Expenses', color: EXPENSE_COLOR }]} />
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <BarChart
                height={300}
                xAxis={xBand(0.45)}
                yAxis={yMoney}
                series={[
                  { id: 'income', label: 'Income', data: rows.map(r => r.income), color: INCOME_COLOR, valueFormatter: money },
                  { id: 'expense', label: 'Expenses', data: rows.map(r => r.expense), color: EXPENSE_COLOR, valueFormatter: money },
                ]}
                grid={{ horizontal: true }}
                borderRadius={4}
                margin={{ top: 12, right: 8, bottom: 28, left: 56 }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          </ChartCard>

          {/* 2. Categories, month by month */}
          <ChartCard
            data-tour="analysis-categories"
            title={`${typeLabel} by Category, Month by Month`}
            subtitle={`Your ${stackNames.length} biggest ${type === 'expense' ? 'spending' : 'income'} categories${otherNames.length ? '; the rest are grouped as Other' : ''}. Hover a bar for the amounts.`}
          >
            <Legend items={stackSeries.map(s => ({ label: s.label, color: s.color }))} />
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <BarChart
                height={320}
                xAxis={xBand(0.68)}
                yAxis={yMoney}
                series={stackSeries}
                grid={{ horizontal: true }}
                borderRadius={4}
                margin={{ top: 12, right: 8, bottom: 28, left: 56 }}
                slotProps={{ legend: { hidden: true } }}
                sx={chartSx}
              />
            </Box>
          </ChartCard>

          {/* 3. Ranked categories + trend of the picked one */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' }, gap: 3, mb: 3, alignItems: 'start' }}>
            <ChartCard
              data-tour="analysis-ranking"
              title={`Top ${type === 'expense' ? 'Spending' : 'Income'} Categories`}
              subtitle={`Total for the period. Click a category to see its trend.`}
              style={{ marginBottom: 0 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }} role="group" aria-label={`${typeLabel} categories`}>
                {ranked.map((c) => {
                  const selected = c.name === trendCategory;
                  return (
                    <Box
                      key={c.name}
                      component="button"
                      type="button"
                      onClick={() => setSelectedCategory(c.name)}
                      aria-pressed={selected}
                      aria-label={`${c.name}: ${formatRupees(c.total)}, ${formatPercent(c.share)} of ${typeLabel.toLowerCase()}`}
                      sx={{
                        all: 'unset', boxSizing: 'border-box', cursor: 'pointer', width: '100%', px: 1, py: 0.75, borderRadius: 1.5,
                        bgcolor: selected ? (t) => alpha(t.palette.primary.main, 0.08) : 'transparent',
                        outline: selected ? '1px solid' : 'none', outlineColor: (t) => alpha(t.palette.primary.main, 0.3),
                        '&:hover': { bgcolor: 'action.hover' },
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, alignSelf: 'center', bgcolor: colors[c.name] || OTHER_COLOR }} />
                        <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 600, flex: 1, minWidth: 0 }} noWrap>{c.name}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatRupees(c.total)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatPercent(c.share)}</Typography>
                      </Box>
                      {/* Ranked bar: one hue, square at the baseline, rounded at the data end */}
                      <Box sx={{ height: 8, borderRadius: '0 4px 4px 0', bgcolor: INCOME_COLOR, opacity: selected ? 1 : 0.75, width: `${Math.max(1, (c.total / maxRanked) * 100)}%` }} />
                    </Box>
                  );
                })}
              </Box>
            </ChartCard>

            <ChartCard
              data-tour="analysis-trend"
              title={trendCategory ? `${trendCategory}: Month by Month` : 'Category Trend'}
              subtitle={trendCategory ? `${formatRupees(trendData.reduce((a, b) => a + b, 0))} in total · average ${formatRupees(trendAverage)} a month (the line)` : ''}
              style={{ marginBottom: 0 }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <BarChart
                  height={300}
                  xAxis={xBand(0.62)}
                  yAxis={yMoney}
                  series={[{ id: 'trend', label: trendCategory || '', data: trendData, color: INCOME_COLOR, valueFormatter: money }]}
                  grid={{ horizontal: true }}
                  borderRadius={4}
                  margin={{ top: 12, right: 8, bottom: 28, left: 56 }}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{ ...chartSx, '& .MuiChartsReferenceLine-line': { stroke: '#334155', strokeWidth: 1 } }}
                >
                  {trendAverage > 0 && (
                    <ChartsReferenceLine
                      y={trendAverage}
                      label={`Avg ${compactRupees(trendAverage)}`}
                      labelAlign="end"
                      labelStyle={{ fontSize: 11, fill: '#334155' }}
                    />
                  )}
                </BarChart>
              </Box>
            </ChartCard>
          </Box>

          {/* 4. The same numbers as a table */}
          <ChartCard data-tour="analysis-table" title="Month by Month" subtitle="Every number from the charts above.">
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small" aria-label="Income and expenses by month">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Income</TableCell>
                    <TableCell align="right">Expenses</TableCell>
                    <TableCell align="right">Saved</TableCell>
                    <TableCell align="right">Savings rate</TableCell>
                    <TableCell>Top {type === 'expense' ? 'expense' : 'income'} category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...rows].reverse().map(r => (
                    <TableRow key={r.key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.longLabel}</TableCell>
                      <TableCell align="right">{formatRupees(r.income)}</TableCell>
                      <TableCell align="right">{formatRupees(r.expense)}</TableCell>
                      <TableCell align="right" sx={{ color: r.net < 0 ? 'error.main' : 'text.primary', fontWeight: 600 }}>{formatRupees(r.net)}</TableCell>
                      <TableCell align="right">{formatPercent(r.rate)}</TableCell>
                      <TableCell>{r.topCategory ? `${r.topCategory} (${formatRupees(r.categories[r.topCategory])})` : '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderTopColor: 'divider' } }}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{formatRupees(totals.income)}</TableCell>
                    <TableCell align="right">{formatRupees(totals.expense)}</TableCell>
                    <TableCell align="right" sx={{ color: totals.net < 0 ? 'error.main' : 'text.primary' }}>{formatRupees(totals.net)}</TableCell>
                    <TableCell align="right">{formatPercent(totals.rate)}</TableCell>
                    <TableCell>{ranked[0] ? `${ranked[0].name} (${formatRupees(ranked[0].total)})` : '—'}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Box>
      )}

      <GuidedTour steps={TOURS.analysis} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default TransactionAnalysis;
